// Tracks admin re-checks that are still settling server-side, scoped by
// campaign, entry, and scope (profile lookup vs. reel metrics) so the two
// coexist on one row without clobbering each other's timer or baseline. A
// row is settling while it still shows the baseline it had when the
// re-check was queued (status + checked-at), bounded by the server cooldown
// the 202 returned (profile) or the stall window (metrics). A re-check that confirms the same status still lands —
// the BE bumps checked-at — so the spinner stops as soon as the worker
// runs, not at the end of the cooldown. Rows subscribe to their own entry,
// so a mark re-renders one cell — not the whole table.
import { create } from 'zustand';
import type { Query } from '@tanstack/react-query';
import type { RosterPage } from '../../hooks';
import { rowLookup } from './creatorLookupStatus';
import { rowMetrics, type MetricsBaseline } from './creatorMetricsStatus';
import type { CreatorsRow } from './types';

/** Matches the profile-sync worker's poll: refetching faster finds nothing new. */
export const PROFILE_RECHECK_POLL_INTERVAL_MS = 30_000;
const MS_PER_SECOND = 1_000;
/** Bounds on the server-reported cooldown, so a bad value can't pin a row. */
export const MAX_RECHECK_WINDOW_SECONDS = 3_600;
export const DEFAULT_RECHECK_WINDOW_SECONDS = 300;

export type RecheckScope = 'profile' | 'metrics';
export type Baseline = MetricsBaseline;
type RowBaselines = Partial<Record<RecheckScope, Baseline>>;
/** campaign id → entry id → scope → the baseline when that scope's re-check was queued. */
type CampaignBaselines = Readonly<Record<string, RowBaselines>>;
type Baselines = Readonly<Record<string, CampaignBaselines>>;

interface ProfileRecheckState {
  baselines: Baselines;
  markRechecking: (campaignId: string, entryId: string, scope: RecheckScope, baseline: Baseline, windowSeconds: number) => void;
  clearRechecking: (campaignId: string, entryId: string, scope: RecheckScope) => void;
}

function isSameBaseline(a: Baseline, b: Baseline): boolean {
  return a.status === b.status && a.checkedAt === b.checkedAt;
}

/** 'pending' is the metrics endpoint's FIRST write, not an outcome: on raw
 *  baseline equality a queued job stops being "settling" on the very first
 *  poll, well before the multi-minute fetch lands. Only a terminal status
 *  settles it — the clamped expiry timer still bounds the wait. The profile
 *  scope has no such intermediate write and keeps plain baseline equality. */
const METRICS_NON_TERMINAL_STATUS = 'pending';

function isStillSettling(scope: RecheckScope, baseline: Baseline, current: Baseline): boolean {
  if (scope === 'metrics' && current.status === METRICS_NON_TERMINAL_STATUS) return true;
  return isSameBaseline(baseline, current);
}

const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();
function timerKey(campaignId: string, entryId: string, scope: RecheckScope): string {
  return `${campaignId}:${entryId}:${scope}`;
}

function withScope(baselines: Baselines, campaignId: string, entryId: string, scope: RecheckScope, baseline: Baseline): Baselines {
  const row = baselines[campaignId]?.[entryId] ?? {};
  return { ...baselines, [campaignId]: { ...baselines[campaignId], [entryId]: { ...row, [scope]: baseline } } };
}

function withoutScope(baselines: Baselines, campaignId: string, entryId: string, scope: RecheckScope): Baselines {
  const row = baselines[campaignId]?.[entryId];
  if (!row || !(scope in row)) return baselines;
  const { [scope]: _removed, ...rest } = row;
  const otherEntries = Object.entries(baselines[campaignId] ?? {}).filter(([id]) => id !== entryId);
  const campaign = Object.keys(rest).length === 0 ? otherEntries : [...otherEntries, [entryId, rest] as const];
  if (campaign.length === 0) {
    return Object.fromEntries(Object.entries(baselines).filter(([id]) => id !== campaignId));
  }
  return { ...baselines, [campaignId]: Object.fromEntries(campaign) };
}

/** Clamps the 202's retry_after_seconds to a sane timer duration. */
export function recheckWindowSeconds(windowSeconds: number): number {
  if (!Number.isFinite(windowSeconds)) return DEFAULT_RECHECK_WINDOW_SECONDS;
  return Math.min(Math.max(windowSeconds, 0), MAX_RECHECK_WINDOW_SECONDS);
}

export const useProfileRecheckStore = create<ProfileRecheckState>((set, get) => ({
  baselines: {},
  markRechecking: (campaignId, entryId, scope, baseline, windowSeconds) => {
    const key = timerKey(campaignId, entryId, scope);
    clearTimeout(expiryTimers.get(key));
    expiryTimers.set(
      key,
      setTimeout(
        () => get().clearRechecking(campaignId, entryId, scope),
        recheckWindowSeconds(windowSeconds) * MS_PER_SECOND,
      ),
    );
    set((state) => ({ baselines: withScope(state.baselines, campaignId, entryId, scope, baseline) }));
  },
  clearRechecking: (campaignId, entryId, scope) => {
    const key = timerKey(campaignId, entryId, scope);
    clearTimeout(expiryTimers.get(key));
    expiryTimers.delete(key);
    set((state) => ({ baselines: withoutScope(state.baselines, campaignId, entryId, scope) }));
  },
}));

/** A re-check is settling while the row still shows its queued-time baseline
 *  for that scope — or, for metrics, while it sits on a non-terminal status. */
export function isRecheckSettling(
  baselines: Baselines,
  campaignId: string,
  entryId: string,
  scope: RecheckScope,
  current: Baseline,
): boolean {
  const baseline = baselines[campaignId]?.[entryId]?.[scope];
  return !!baseline && isStillSettling(scope, baseline, current);
}

/** Per-row subscription — re-renders only when this row's settling flips. */
export function useIsRechecking(campaignId: string, entryId: string, scope: RecheckScope, current: Baseline): boolean {
  return useProfileRecheckStore((state) => isRecheckSettling(state.baselines, campaignId, entryId, scope, current));
}

/** True while any re-check in this campaign — either scope — is being
 *  tracked — gates the Creators-tab poll. */
export function useHasProfileRecheck(campaignId: string): boolean {
  return useProfileRecheckStore((state) => campaignId in state.baselines);
}

type CreatorsPageQuery = Query<RosterPage<CreatorsRow>>;

function pageHasSettlingRow(campaignId: string, query: CreatorsPageQuery): boolean {
  const { baselines } = useProfileRecheckStore.getState();
  const rows = query.state.data?.entries ?? [];
  return rows.some(
    (row) =>
      isRecheckSettling(baselines, campaignId, row.id, 'profile', rowLookup(row)) ||
      isRecheckSettling(baselines, campaignId, row.id, 'metrics', rowMetrics(row.snapshot_metrics)),
  );
}

/** react-query refetchInterval for a Creators page: polls only while a row on
 *  it is still settling in either scope, so it stops the moment a newer
 *  fetch lands. */
export function profileRecheckRefetchInterval(
  campaignId: string,
  hasRecheck: boolean,
): false | ((query: CreatorsPageQuery) => number | false) {
  if (!hasRecheck) return false;
  return (query) => (pageHasSettlingRow(campaignId, query) ? PROFILE_RECHECK_POLL_INTERVAL_MS : false);
}
